import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useLocation } from 'react-router-dom';

const ProductivityContext = createContext(null);

// Alarm sound URLs (Web Audio API beeps)
const ALARM_SOUNDS = {
  classic: { name: 'Classic Beep', frequency: 800, pattern: [200, 100, 200, 100, 200, 100, 200] },
  urgent: { name: 'Urgent Alert', frequency: 1000, pattern: [100, 50, 100, 50, 100, 200, 100, 50, 100, 50, 100] },
  gentle: { name: 'Gentle Reminder', frequency: 600, pattern: [300, 200, 300, 200, 300] },
};

export function ProductivityProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [isIdle, setIsIdle] = useState(false);
  const [idleTime, setIdleTime] = useState(0);
  const [idleStartTime, setIdleStartTime] = useState(null);
  const [settings, setSettings] = useState({
    idleTimeout: 55,
    alarmSound: 'classic',
    alarmVolume: 0.8,
    idleTrackingEnabled: true,
  });
  const [totalIdleEvents, setTotalIdleEvents] = useState(0);
  const [sessionIdleTime, setSessionIdleTime] = useState(0);
  
  const idleTimerRef = useRef(null);
  const idleCounterRef = useRef(null);
  const audioContextRef = useRef(null);
  const alarmIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Load settings from backend
  const loadSettings = useCallback(async () => {
    if (!user) return;
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/productivity/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          idleTimeout: data.idle_timeout || 55,
          alarmSound: data.alarm_sound || 'classic',
          alarmVolume: data.alarm_volume || 0.8,
          idleTrackingEnabled: data.idle_tracking_enabled !== false,
        }));
      }
    } catch (error) {
      console.error('Failed to load productivity settings:', error);
    }
  }, [user]);

  // Initial load
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Play alarm sound using Web Audio API
  const playAlarm = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const ctx = audioContextRef.current;
    const soundConfig = ALARM_SOUNDS[settings.alarmSound] || ALARM_SOUNDS.classic;
    
    let currentTime = ctx.currentTime;
    
    const playBeep = () => {
      soundConfig.pattern.forEach((duration, index) => {
        if (index % 2 === 0) {
          const oscillator = ctx.createOscillator();
          const gainNode = ctx.createGain();
          
          oscillator.connect(gainNode);
          gainNode.connect(ctx.destination);
          
          oscillator.frequency.value = soundConfig.frequency;
          oscillator.type = 'square';
          gainNode.gain.value = settings.alarmVolume;
          
          oscillator.start(currentTime);
          oscillator.stop(currentTime + duration / 1000);
        }
        currentTime += duration / 1000;
      });
    };

    playBeep();
    
    // Repeat alarm every 2 seconds
    alarmIntervalRef.current = setInterval(() => {
      currentTime = ctx.currentTime;
      playBeep();
    }, 2000);
  }, [settings.alarmSound, settings.alarmVolume]);

  // Stop alarm
  const stopAlarm = useCallback(() => {
    if (alarmIntervalRef.current) {
      clearInterval(alarmIntervalRef.current);
      alarmIntervalRef.current = null;
    }
  }, []);

  // Log activity to backend
  const logActivity = useCallback(async (activityType, details = {}) => {
    if (!user) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      await fetch(`${API_URL}/api/productivity/log`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          activity_type: activityType,
          page: location.pathname,
          details: details,
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('Failed to log activity:', error);
    }
  }, [user, location.pathname]);

  // Log idle event
  const logIdleEvent = useCallback(async (idleDuration, page) => {
    if (!user) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      await fetch(`${API_URL}/api/productivity/idle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idle_duration_seconds: idleDuration,
          page: page,
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('Failed to log idle event:', error);
    }
  }, [user]);

  // Handle user becoming idle
  const handleIdle = useCallback(() => {
    if (!settings.idleTrackingEnabled || !user || user.role === 'super_admin' || user.role === 'admin') {
      return;
    }
    
    setIsIdle(true);
    setIdleStartTime(Date.now());
    setTotalIdleEvents(prev => prev + 1);
    playAlarm();
    
    // Start counting idle time
    idleCounterRef.current = setInterval(() => {
      setIdleTime(prev => prev + 1);
    }, 1000);
  }, [settings.idleTrackingEnabled, user, playAlarm]);

  // Handle user returning from idle (clicking STOP)
  const handleStopIdle = useCallback(() => {
    if (!isIdle) return;
    
    const idleDuration = Math.floor((Date.now() - idleStartTime) / 1000);
    
    // Log the idle event
    logIdleEvent(idleDuration, location.pathname);
    
    // Update session totals
    setSessionIdleTime(prev => prev + idleDuration);
    
    // Reset idle state
    setIsIdle(false);
    setIdleTime(0);
    setIdleStartTime(null);
    stopAlarm();
    
    if (idleCounterRef.current) {
      clearInterval(idleCounterRef.current);
      idleCounterRef.current = null;
    }
    
    // Reset activity timer
    lastActivityRef.current = Date.now();
    resetIdleTimer();
  }, [isIdle, idleStartTime, location.pathname, logIdleEvent, stopAlarm]);

  // Reset idle timer
  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    
    if (!settings.idleTrackingEnabled || !user || user.role === 'super_admin' || user.role === 'admin') {
      return;
    }
    
    idleTimerRef.current = setTimeout(() => {
      handleIdle();
    }, settings.idleTimeout * 1000);
  }, [settings.idleTimeout, settings.idleTrackingEnabled, user, handleIdle]);

  // Track user activity
  const handleActivity = useCallback(() => {
    if (isIdle) return; // Don't reset if already idle (need to click STOP)
    
    lastActivityRef.current = Date.now();
    resetIdleTimer();
  }, [isIdle, resetIdleTimer]);

  // Set up activity listeners
  useEffect(() => {
    if (!user || user.role === 'super_admin' || user.role === 'admin') {
      return;
    }

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    
    resetIdleTimer();
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
      if (idleCounterRef.current) {
        clearInterval(idleCounterRef.current);
      }
      stopAlarm();
    };
  }, [user, handleActivity, resetIdleTimer, stopAlarm]);

  // Log page navigation
  useEffect(() => {
    if (user) {
      logActivity('page_view', { page: location.pathname });
    }
  }, [location.pathname, user, logActivity]);

  // Format time for display
  const formatIdleTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || hours > 0) parts.push(`${minutes}m`);
    parts.push(`${secs}s`);
    
    return parts.join(' ');
  };

  const value = {
    isIdle,
    idleTime,
    totalIdleEvents,
    sessionIdleTime,
    settings,
    setSettings,
    handleStopIdle,
    logActivity,
    formatIdleTime,
    refreshSettings: loadSettings,
    ALARM_SOUNDS,
  };

  return (
    <ProductivityContext.Provider value={value}>
      {children}
    </ProductivityContext.Provider>
  );
}

export function useProductivity() {
  const context = useContext(ProductivityContext);
  if (!context) {
    throw new Error('useProductivity must be used within a ProductivityProvider');
  }
  return context;
}

export default ProductivityContext;
